<template>
    <div class="-mt-8 md:mt-0 w-full">

        <h1 class="mb-4 leading-tight hidden md:block">
            {{ product.name }}
        </h1>

        <div class="flex items-center gap-4 md:gap-8">
            <div class="flex items-center justify-between gap-4 md:gap-8 w-full">
                <div class="text-2xl text-grey-dark font-bold">
                    <OfferPrice
                        v-if="totalPrice > 0"
                        :price="totalPrice"
                    />
                    <OfferPrice
                        v-else
                        :price="product.from_price"
                        :offer-price="product.offer_price"
                        :offer-name="product.offer_name"
                        :show-from="product.price !== product.from_price || product.is_enquiry"
                        :to-hire="product.is_hire"
                    />
                </div>

                <ToggleFavourite
                    :product-id="product.id"
                    class="w-10 h-10 p-1 bg-grey-lighter"
                />

                <div
                    v-if="showFreeShippingOnMobile"
                    class="text-sm bg-pink text-white rounded px-2 py-1 md:hidden font-bold"
                >
                    Free shipping on mobile
                </div>
            </div>

            <div
                v-if="product.active && product.in_stock && productAvailableForDelivery && !showVariationsInPage"
                class="w-full"
            >
                <div v-if="product.is_hire">
                    <Button
                        class="w-full"
                        :text="product.button_label"
                        @click="handleHireButtonClick"
                    >
                        <template #icon>
                            <EnvelopeIcon class="h-5 w-auto mr-2" />
                        </template>
                    </Button>
                </div>
                <div v-else-if="product.is_enquiry">
                    <Button
                        class="w-full"
                        :text="product.button_label"
                        @click="handleEnquiryButtonClick"
                    >
                        <template #icon>
                            <EnvelopeIcon class="h-5 w-auto mr-2" />
                        </template>
                    </Button>
                </div>
                <div v-else-if="product.has_variations">
                    <Button
                        class="w-full"
                        :text="product.button_label"
                        @click="handleBuyButtonClick"
                    >
                        <template #icon>
                            <BasketIcon class="h-5 mr-2" />
                        </template>
                    </Button>
                </div>
                <div v-else-if="!showVariationsInPage">
                    <Button
                        v-show="!quantityInBasket"
                        :loading="isBuyButtonLoading"
                        class="w-full"
                        :text="product.button_label"
                        @click="handleBuyButtonClick"
                    >
                        <template #icon>
                            <BasketIcon class="h-5 mr-2" />
                        </template>
                    </Button>
                    <ProductQuantityInput
                        v-show="quantityInBasket"
                        :product-id="product.id"
                        :quantity="quantityInBasket"
                        :show-checkout-button="true"
                    />
                </div>
            </div>

            <ProductReviewsScore
                v-if="product.reviews_count"
                :product-id="product.id"
                :score="product.reviews_average_score"
                :count="product.reviews_count"
                clickable
            />
        </div>

        <div v-if="product.active && product.in_stock">

            <div v-if="product.check_delivery_postcode">

                <AddressCheckerResults
                    v-if="addressChecked"
                    class="mb-5"
                    :available="productAvailableForDelivery"
                    :postcode="postcode"
                    :price="deliveryPrice"
                    :cross-sells="product.alternatives"
                />

                <ProductAddressChecker
                    v-else
                    :product="product"
                    :shipping-class="product.shipping_class_id"
                />
            </div>
        </div>
        <div
            v-else
            class="text-red-500"
        >
            <p class="mb-3">
                <b>This product is currently unavailable.</b>
            </p>

            <ProductStockNotification :product="product" />

            <p
                v-if="product.cross_sells.length > 0"
                class="my-4"
            >
                Why not check out some of these alternative products?
            </p>
            <div class="grid grid-cols-2 gap-4">
                <ProductCard
                    v-for="crossSell in product.cross_sells ?? []"
                    :key="crossSell.id"
                    :product="crossSell"
                />
            </div>
        </div>

        <ProductVariations
            v-if="showVariationsInPage && product.active && product.in_stock && productAvailableForDelivery"
            :product="product"
            :show-preview="false"
        />

        <div
            v-if="product.description"
            class="html mb-8"
            v-html="product.description"
        />

        <client-only>
            <HireEnquiryModal
                :product="product"
            />
            <ProductEnquiryModal
                :product="product"
            />
        </client-only>
    </div>
</template>

<script>
import Button from '~/components/Button';
import ProductQuantityInput from '~/components/ProductQuantityInput';
import ProductReviewsScore from '~/components/ProductReviewsScore';
import BasketIcon from '~/static/fonts/LineIcons/shopping-basket.svg';
import EnvelopeIcon from '~/static/fonts/LineIcons/envelope.svg';
import ProductAddressChecker from './ProductAddressChecker';
import AddressCheckerResults from './AddressCheckerResults';
import ProductStockNotification from './ProductStockNotification';
import handleException from '../plugins/error-handler.client';
import OfferPrice from './OfferPrice';
import HireEnquiryModal from '@/components/HireEnquiryModal.vue';
import ProductEnquiryModal from '@/components/ProductEnquiryModal.vue';
import ProductCustomisationVariationInput from '@/components/ProductCustomisationVariationInput.vue';
import ProductVariations from '@/components/ProductVariations.vue';
import ProductCard from '@/components/ProductCard.vue';
import ToggleFavourite from './ToggleFavourite.vue';

export default {

    components: {
        ToggleFavourite,
        ProductCard,
        ProductVariations,
        ProductCustomisationVariationInput,
        HireEnquiryModal,
        ProductEnquiryModal,
        ProductAddressChecker,
        ProductStockNotification,
        AddressCheckerResults,
        Button,
        ProductQuantityInput,
        ProductReviewsScore,
        BasketIcon,
        EnvelopeIcon,
        OfferPrice,
    },

    props: {
        product: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            isBuyButtonLoading: false,
        };
    },

    computed: {
        totalPrice() {
            return this.$store.getters['product/totalPrice'];
        },
        quantityInBasket() {
            const basketItems = this.$store.getters['basket/items'];
            const currentProduct = basketItems.find((item) => item.product.id === this.product.id);
            return currentProduct ? currentProduct.quantity : 0;
        },
        addressChecked() {
            return this.$store.getters['checkout/addressChecked'](this.product.shipping_class_id);
        },
        productAvailableForDelivery() {
            if (!this.product.check_delivery_postcode) {
                return true;
            }

            return this.$store.getters['checkout/checkProductAvailable'](this.product.shipping_class_id);
        },
        postcode() {
            return this.$store.getters['checkout/shippingAddress'].value.postcode;
        },
        deliveryPrice() {
            return this.$store.getters['checkout/getDeliveryPrice'](this.product.shipping_class_id);
        },
        showFreeShippingOnMobile() {
            return this.$store.getters['settings/getByKey']('mobile_discount_code') && !this.product.check_delivery_postcode;
        },
        showVariationsInPage() {
            return !(this.product.is_hire || this.product.is_enquiry);
        },
    },

    methods: {
        handleBuyButtonClick() {
            if (this.product.has_variations) {
                this.$modal.show('product-customisation-modal', {
                    product: this.product,
                });
            } else {
                this.isBuyButtonLoading = true;
                this.$store.dispatch('basket/updateItem', {
                    productId: this.product.id,
                    quantity: 1,
                }).then(() => {
                    this.isBuyButtonLoading = false;
                    this.$modal.show('product-added-to-basket-modal', {
                        product: this.product,
                    });
                }).catch((error) => {
                    this.isBuyButtonLoading = false;
                    handleException(error);
                });
            }
        },
        handleHireButtonClick() {
            this.$modal.show('hire-enquiry-modal');
        },
        handleEnquiryButtonClick() {
            this.$modal.show('product-enquiry-modal');
        },
        resetDeliveryAddress() {
            this.$store.dispatch('checkout/resetProductsChecked');
        },
    },

};
</script>

<style lang="scss" scoped>
</style>
